import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Form } from 'react-bootstrap'
import { Modal } from '../Modal/Modal'
import { Button } from '../Button/Button'
import { Spinner } from '../Spinner/Spinner'
import { HumanInputFormView } from './HumanInputFormView'
import { isRunFinished } from './runStatus'
import { buildRunInputs } from './runInputs'
import { RunResultView } from './RunResultView'
import { usePendingRunForm } from './usePendingRunForm'
import pickerStyles from './CustomWorkflows.module.scss'
import classNames from 'classnames'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useCustomWorkflowsEnabled } from '../../common/useCustomWorkflowsEnabled'
import {
  CustomWorkflowContext,
  CustomWorkflowFileReference,
  CustomWorkflowInputField,
  type CurrentStep,
  CustomWorkflowRun,
  useGetCustomWorkflowRunQuery,
  useLazyGetCustomWorkflowQuery,
  useListCustomWorkflowsQuery,
  useStartCustomWorkflowRunMutation,
  useUploadCustomWorkflowFileMutation,
} from '../../store/customWorkflow/customWorkflows'

export interface CustomWorkflowRunModalProps {
  show: boolean
  onHide: () => void
  scope: string
  workflowId?: string
  context?: CustomWorkflowContext
  contextPreviews?: Record<string, React.ReactNode>
  title?: string
}

const MAX_VISIBLE_STEPS = 5

interface CurrentStepGroup {
  title: string
  desc: string | null
  count: number
}

const groupActiveSteps = (steps: CurrentStep[]): CurrentStepGroup[] => {
  const groups: CurrentStepGroup[] = []
  const byTitle = new Map<string, CurrentStepGroup>()
  for (const step of steps) {
    const existing = byTitle.get(step.title)
    if (existing) {
      existing.count += 1
      continue
    }
    const group: CurrentStepGroup = { title: step.title, desc: step.desc, count: 1 }
    byTitle.set(step.title, group)
    groups.push(group)
  }
  return groups
}

const fieldDefault = (field: CustomWorkflowInputField): string => (field.default !== null && field.default !== undefined ? String(field.default) : '')

const InputControl: React.FC<{
  field: CustomWorkflowInputField
  value: string
  onChange: (value: string) => void
}> = ({ field, value, onChange }) => {
  if (field.type === 'select') {
    return (
      <Form.Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value=''>Select…</option>
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Form.Select>
    )
  }
  if (field.type === 'paragraph') {
    return <Form.Control as='textarea' rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
  }
  return <Form.Control type={field.type === 'number' ? 'number' : 'text'} value={value} onChange={(e) => onChange(e.target.value)} />
}

const FileInputControl: React.FC<{
  field: CustomWorkflowInputField
  accountId: string
  workflowId: string
  onChange: (value: CustomWorkflowFileReference | CustomWorkflowFileReference[] | undefined) => void
}> = ({ field, accountId, workflowId, onChange }) => {
  const [upload, { isLoading }] = useUploadCustomWorkflowFileMutation()
  const [names, setNames] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const multiple = field.type === 'file-list'

  const onFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      setNames([])
      onChange(undefined)
      return
    }
    setError(null)
    try {
      const uploaded = await Promise.all(Array.from(fileList).map((file) => upload({ accountId, workflowId, file }).unwrap()))
      setNames(uploaded.map((item) => item.name))
      onChange(multiple ? uploaded.map((item) => item.fileReference) : uploaded[0].fileReference)
    } catch {
      setError('Upload failed.')
      setNames([])
      onChange(undefined)
    }
  }

  return (
    <>
      <Form.Control type='file' multiple={multiple} onChange={(e) => void onFiles((e.target as HTMLInputElement).files)} />
      {isLoading && <Spinner size='sm' />}
      {names.length > 0 && <div className='zocks-text-gray-600 paragraph-small mt-1'>{names.join(', ')}</div>}
      {error && <div className='zocks-text-danger paragraph-small mt-1'>{error}</div>}
    </>
  )
}

export const CustomWorkflowRunModal: React.FC<CustomWorkflowRunModalProps> = ({ show, onHide, scope, workflowId, context, contextPreviews, title }) => {
  const accountId = useAppSelector(selectAccountId)
  const { enabled: customWorkflowsEnabled } = useCustomWorkflowsEnabled()
  const contextValues = useMemo(() => context ?? {}, [context])
  const previews = useMemo(() => contextPreviews ?? {}, [contextPreviews])

  const {
    data: workflows,
    isLoading: workflowsLoading,
    isError: workflowsError,
  } = useListCustomWorkflowsQuery(
    { accountId: accountId, scope, triggerType: 'manual' },
    { skip: !accountId || !show || !!workflowId || !customWorkflowsEnabled },
  )

  const [triggerGetWorkflow, { data: workflow, isFetching: schemaLoading, isError: schemaError }] = useLazyGetCustomWorkflowQuery()
  const [runWorkflow, { isLoading: running }] = useStartCustomWorkflowRunMutation()

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(workflowId ?? null)
  const [formValues, setFormValues] = useState<Record<string, string>>({})
  const [fileValues, setFileValues] = useState<Record<string, CustomWorkflowFileReference | CustomWorkflowFileReference[]>>({})
  const [runResult, setRunResult] = useState<CustomWorkflowRun | null>(null)
  const [runError, setRunError] = useState<string | null>(null)
  const [terminalRunId, setTerminalRunId] = useState<string | null>(null)
  const [finalRun, setFinalRun] = useState<CustomWorkflowRun | null>(null)
  const [stickySteps, setStickySteps] = useState<{ runId: string | null; signature: string; groups: CurrentStepGroup[] }>({
    runId: null,
    signature: '',
    groups: [],
  })

  const identity = `${accountId ?? ''}|${scope}|${workflowId ?? ''}`
  const [prevIdentity, setPrevIdentity] = useState(identity)
  if (prevIdentity !== identity) {
    setPrevIdentity(identity)
    setSelectedWorkflowId(workflowId ?? null)
    setFormValues({})
    setFileValues({})
    setRunResult(null)
    setRunError(null)
    setTerminalRunId(null)
    setFinalRun(null)
  }

  const activeRunId = runResult?.id ?? null
  const { data: polledRun } = useGetCustomWorkflowRunQuery(
    { accountId: accountId, workflowId: selectedWorkflowId ?? '', runId: activeRunId ?? '' },
    { skip: !accountId || !selectedWorkflowId || !activeRunId || !show || terminalRunId === activeRunId, pollingInterval: 2000, skipPollingIfUnfocused: true },
  )
  const currentRun = finalRun ?? polledRun ?? runResult
  const paused = currentRun?.status === 'paused'
  const finished = !!currentRun && isRunFinished(currentRun.status)
  const stepGroups = groupActiveSteps(currentRun?.currentSteps ?? [])
  const stepSignature = (currentRun?.currentSteps ?? []).map((step) => step.title).join('\u0001')
  if (stepGroups.length > 0 && (stickySteps.runId !== activeRunId || stickySteps.signature !== stepSignature)) {
    setStickySteps({ runId: activeRunId, signature: stepSignature, groups: stepGroups })
  }
  const displayStepGroups = stepGroups.length > 0 ? stepGroups : stickySteps.runId === activeRunId ? stickySteps.groups : []
  const visibleStepGroups = displayStepGroups.slice(0, MAX_VISIBLE_STEPS)
  const hiddenStepCount = displayStepGroups.length - visibleStepGroups.length
  if (finished && activeRunId && terminalRunId !== activeRunId) {
    setTerminalRunId(activeRunId)
    setFinalRun(currentRun)
  }

  const pendingInput = usePendingRunForm({ accountId, workflowId: selectedWorkflowId ?? '', runId: activeRunId, run: currentRun })

  const onSelectWorkflow = (id: string) => {
    setSelectedWorkflowId(id)
    setFormValues({})
    setFileValues({})
    setRunResult(null)
    setRunError(null)
    setTerminalRunId(null)
    setFinalRun(null)
    if (accountId) {
      void triggerGetWorkflow({ accountId, workflowId: id })
    }
  }

  useEffect(() => {
    if (show && workflowId && accountId) {
      void triggerGetWorkflow({ accountId, workflowId })
    }
  }, [show, workflowId, accountId, triggerGetWorkflow])

  const onRun = async () => {
    if (!accountId || !workflow) {
      return
    }
    setRunResult(null)
    setRunError(null)
    setTerminalRunId(null)
    setFinalRun(null)
    const inputs = buildRunInputs(workflow.inputSchema, { context: contextValues, formValues, fileValues })
    try {
      const result = await runWorkflow({ accountId, workflowId: workflow.id, inputs }).unwrap()
      setRunResult(result)
    } catch (err) {
      const message = (err as { data?: { errors?: Array<{ message?: string }> } })?.data?.errors?.[0]?.message
      setRunError(message ?? 'Failed to run the workflow.')
    }
  }

  const workflowLoaded = !!workflow && workflow.id === selectedWorkflowId && !schemaLoading
  const runStarted = !!runResult
  const requiredMet =
    !!workflow &&
    workflow.inputSchema
      .filter((field) => !field.hidden && field.required)
      .every((field) =>
        field.type === 'file' || field.type === 'file-list'
          ? fileValues[field.variable] !== undefined
          : (formValues[field.variable] ?? fieldDefault(field)).trim() !== '',
      )
  const header = <h5 className='mb-0'>{title ?? workflow?.name ?? 'Workflows'}</h5>

  const body = (
    <>
      {!workflowId && !selectedWorkflowId && (
        <>
          {workflowsLoading && <Spinner size='sm' />}
          {workflowsError && <Alert variant='warning'>Could not load workflows.</Alert>}
          {!workflowsLoading && !workflowsError && (workflows?.length ?? 0) === 0 && <p className='zocks-text-gray-600 mb-0'>No workflows available.</p>}
          {(workflows?.length ?? 0) > 0 && (
            <div className='d-flex flex-column zocks-gap-2'>
              {workflows!.map((item) => (
                <Button
                  key={item.id}
                  type='button'
                  variant='borderless'
                  className={`p-3 text-start w-100 d-flex flex-column align-items-start ${pickerStyles.pickerItem}`}
                  onClick={() => onSelectWorkflow(item.id)}
                >
                  <div className='fw-semibold'>{item.name}</div>
                  {item.description && <div className='paragraph-small zocks-text-gray-600'>{item.description}</div>}
                </Button>
              ))}
            </div>
          )}
        </>
      )}

      {selectedWorkflowId && schemaLoading && <Spinner size='sm' />}
      {selectedWorkflowId && schemaError && !schemaLoading && <Alert variant='warning'>Could not load the workflow.</Alert>}

      {workflowLoaded && !runStarted && (
        <>
          {workflow!.description && (
            <div className='zocks-bg-color-gray-100 rounded-2 p-3 mb-3 zocks-text-gray-600 paragraph-small'>{workflow!.description}</div>
          )}
          <Form>
            {workflow!.inputSchema
              .filter((field) => !field.hidden)
              .map((field) => (
                <Form.Group className='mb-3' key={field.variable} controlId={`workflow-field-${field.variable}`}>
                  <Form.Label>
                    {field.label || field.variable}
                    {field.required && <span className='zocks-text-danger'> *</span>}
                  </Form.Label>
                  {field.type === 'file' || field.type === 'file-list' ? (
                    <FileInputControl
                      field={field}
                      accountId={accountId ?? ''}
                      workflowId={workflow!.id}
                      onChange={(reference) =>
                        setFileValues((prev) => {
                          const next = { ...prev }
                          if (reference === undefined) {
                            delete next[field.variable]
                          } else {
                            next[field.variable] = reference
                          }
                          return next
                        })
                      }
                    />
                  ) : (
                    <InputControl
                      field={field}
                      value={formValues[field.variable] ?? fieldDefault(field)}
                      onChange={(value) => setFormValues((prev) => ({ ...prev, [field.variable]: value }))}
                    />
                  )}
                </Form.Group>
              ))}
            {workflow!.inputSchema
              .filter((field) => contextValues[field.variable] !== undefined && previews[field.variable] !== undefined)
              .map((field) => (
                <div className='mb-3' key={`preview-${field.variable}`}>
                  {previews[field.variable]}
                </div>
              ))}
          </Form>
        </>
      )}

      {runError && (
        <Alert variant='danger' className='mt-3 mb-0'>
          {runError}
        </Alert>
      )}
      {runStarted && !finished && !paused && (
        <div className='mt-3'>
          {visibleStepGroups.length === 0 ? (
            <div className={pickerStyles.runningRow}>
              <span className={pickerStyles.pulseDot} />
              <span>Running…</span>
            </div>
          ) : (
            <div className={pickerStyles.currentSteps}>
              <div className={pickerStyles.runningRow}>
                <span className={pickerStyles.pulseDot} />
                <span className='fw-semibold'>Currently running</span>
              </div>
              <ul className={pickerStyles.stepList}>
                {visibleStepGroups.map((group) => (
                  <li key={group.title} className={pickerStyles.stepItem}>
                    <span className={pickerStyles.stepDot} />
                    <div className={pickerStyles.stepBody}>
                      <span>{group.count > 1 ? `${group.title} ×${group.count}` : group.title}</span>
                      {group.desc ? <div className='paragraph-small zocks-text-gray-600'>{group.desc}</div> : null}
                    </div>
                  </li>
                ))}
                {hiddenStepCount > 0 ? (
                  <li className={classNames(pickerStyles.stepItem, 'paragraph-small', 'zocks-text-gray-600')}>…and {hiddenStepCount} more</li>
                ) : null}
              </ul>
            </div>
          )}
        </div>
      )}
      {paused ? (
        pendingInput.form ? (
          <div className='mt-3'>
            <Alert variant='info'>{pendingInput.locked ? 'Input submitted — continuing the workflow…' : 'This workflow is waiting for your input.'}</Alert>
            <HumanInputFormView
              key={pendingInput.form.id}
              accountId={accountId}
              workflowId={selectedWorkflowId ?? ''}
              form={pendingInput.form}
              submitting={pendingInput.submitting || pendingInput.locked}
              onSubmit={pendingInput.submit}
            />
            {pendingInput.submitError ? (
              <Alert variant='danger' className='mt-3 mb-0'>
                {pendingInput.submitError}
              </Alert>
            ) : null}
          </div>
        ) : pendingInput.awaitingInput && !pendingInput.loadError ? (
          <div className='d-flex align-items-center zocks-gap-2 mt-3'>
            <Spinner size='sm' />
            <span>Loading input…</span>
          </div>
        ) : (
          <Alert variant='warning' className='mt-3 mb-0'>
            Could not load the input form. Please close and reopen this run.
          </Alert>
        )
      ) : null}
      {finished && currentRun && workflow && activeRunId ? (
        <div className='mt-3'>
          <RunResultView
            accountId={accountId}
            workflowId={workflow.id}
            runId={activeRunId}
            workflowName={workflow.name || workflow.id}
            status={currentRun.status}
            createdAt={null}
            finishedAt={null}
            elapsedTime={null}
            workflow={workflow}
            detail={currentRun}
            isLoading={false}
            isError={false}
          />
        </div>
      ) : null}
    </>
  )

  const footer =
    workflowLoaded && !runStarted ? (
      <div className='d-flex zocks-gap-3'>
        {!workflowId && (
          <Button variant='secondary' disabled={running} onClick={() => setSelectedWorkflowId(null)}>
            Back
          </Button>
        )}
        <Button variant='primary' onClick={onRun} loading={running} disabled={running || !requiredMet}>
          {running ? 'Running…' : 'Run'}
        </Button>
      </div>
    ) : null

  return <Modal size='xl' show={show} onHide={onHide} header={header} body={body} footer={footer} bodyClassName={footer ? '' : 'pb-4'} />
}

export default CustomWorkflowRunModal
